/* eslint-disable react-hooks/exhaustive-deps */
import { useGlobalFilter, usePagination, useTable } from 'react-table';
import React, { Fragment, useMemo } from 'react';
import ScrollBar from 'simplebar-react';

import {
  Box,
  Button,
  Grid,
  Stack,
  TableBody,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import { SearchFilter } from 'components/Datatable/index';
import { Heading } from 'components/Typography';
import StyledDatagrid from './Datagrid.styled';
import Layouts from 'layouts/index';
import theme from 'core/ThemeConstants';

const Datagrid = ({
  data,
  columnShape,
  hasToolbar,
  hasPagination,
  onPaginationChange,
  pages,
  pageNumber,
  onAdd,
  sorting = false,
  onSorting,
  sortingDirection,
  sortBy,
}) => {
  const columns = useMemo(() => columnShape, []);
  const tableData = useMemo(() => data, [data]);

  const handleSortDirection = (property) => (event) => {
    onSorting(event, property);
  };

  const {
    rows,
    state,
    prepareRow,
    headerGroups,
    getTableProps,
    setGlobalFilter,
    getTableBodyProps,
  } = useTable(
    {
      columns,
      data: tableData,
    },
    useGlobalFilter,
    usePagination,
  );

  const isEmptyTable = data.length === 0;

  const renderLabel = (column) => {
    const isActiveSort = sortBy === column.id;
    const direction = isActiveSort ? sortingDirection?.toLowerCase() : 'asc';

    return sorting ? (
      <TableSortLabel
        active={isActiveSort}
        direction={direction}
        onClick={handleSortDirection({ by: column.id, type: direction })}
        sx={{
          color: '#fff !important',
          svg: {
            color: '#fff !important',
            marginLeft: '20px !important',
          },
        }}
      >
        {column.render('Header')}
      </TableSortLabel>
    ) : (
      <>{column.render('Header')}</>
    );
  };

  const renderToolbar = hasToolbar ? (
    <Layouts.Flexbox
      py="2rem"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
    >
      <SearchFilter
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
      />
      <Button size="large" color="success" variant="outlined" onClick={onAdd}>
        Add Account
      </Button>
    </Layouts.Flexbox>
  ) : null;

  const renderTableHead = (
    <TableHead>
      {headerGroups.map((headerGroup) => (
        <TableRow {...headerGroup.getHeaderGroupProps()}>
          {headerGroup.headers.map((column) => (
            <StyledDatagrid.TableHeadCell
              {...column.getHeaderProps()}
              sx={{ minWidth: column.minWidth }}
            >
              {renderLabel(column)}
            </StyledDatagrid.TableHeadCell>
          ))}
        </TableRow>
      ))}
    </TableHead>
  );

  const renderTableBody = (
    <TableBody {...getTableBodyProps()}>
      {rows.map((row) => {
        prepareRow(row);

        return (
          <TableRow {...row.getRowProps()}>
            {row.cells.map((cell) => (
              <StyledDatagrid.TableBodyCell {...cell.getCellProps()}>
                {cell.render('Cell')}
              </StyledDatagrid.TableBodyCell>
            ))}
          </TableRow>
        );
      })}
    </TableBody>
  );

  const renderTable = isEmptyTable ? (
    <Box textAlign="center">
      <Heading as="h2" content="No Data" />
    </Box>
  ) : (
    <Fragment>
      {renderTableHead}
      {renderTableBody}
    </Fragment>
  );

  const renderPagination = hasPagination ? (
    <Stack alignItems="center" marginY="2rem">
      <StyledDatagrid.TablePagination
        shape="rounded"
        onChange={onPaginationChange}
        count={pages}
        page={pageNumber}
      />
    </Stack>
  ) : null;

  const renderTableComponent = (
    <Box>
      <Grid container>
        <Grid item xs={12}>
          {renderToolbar}
          <Box sx={{ boxShadow: theme.boxShadows.default }}>
            <ScrollBar>
              <StyledDatagrid.TableGrid {...getTableProps()}>
                {renderTable}
              </StyledDatagrid.TableGrid>
            </ScrollBar>
          </Box>

          {renderPagination}
        </Grid>
      </Grid>
    </Box>
  );

  return renderTableComponent;
};

export default Datagrid;
